import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/**
 * `line-strong` y no `line/70`: el borde de un campo es lo único que dice
 * dónde empieza el campo, así que 1.4.11 le exige 3:1 contra su interior. El
 * gris de separador daba 1,6:1 — un campo que sólo se ve si ya sabes que está.
 *
 * El marcador de posición va a opacidad completa por lo mismo: a 70 % daba
 * 4,06:1, por debajo del 4,5:1 de texto. Y es texto — muchas veces el único
 * que explica qué formato espera el campo.
 */
const CAMPO =
  'w-full rounded-xl border border-line-strong bg-surface-2/50 px-3 py-2 text-sm ' +
  'placeholder:text-muted transition focus:border-primary-soft';

/**
 * Etiqueta y mensaje de error van unidos al control por `id`/`aria-describedby`
 * para que un lector de pantalla anuncie el error al llegar al campo, y no
 * cuando el usuario ya se ha ido de él.
 */
function Envoltura({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label?: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={id} className="block text-sm font-medium text-content">
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const generado = useId();
  const campoId = id ?? generado;

  return (
    <Envoltura id={campoId} label={label} hint={hint} error={error}>
      <input
        ref={ref}
        id={campoId}
        className={cn(CAMPO, error && 'border-danger', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${campoId}-error` : hint ? `${campoId}-hint` : undefined}
        {...rest}
      />
    </Envoltura>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className, id, children, ...rest },
  ref,
) {
  const generado = useId();
  const campoId = id ?? generado;

  return (
    <Envoltura id={campoId} label={label} hint={hint} error={error}>
      <select ref={ref} id={campoId} className={cn(CAMPO, className)} {...rest}>
        {children}
      </select>
    </Envoltura>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const generado = useId();
  const campoId = id ?? generado;

  return (
    <Envoltura id={campoId} label={label} hint={hint} error={error}>
      <textarea ref={ref} id={campoId} className={cn(CAMPO, className)} {...rest} />
    </Envoltura>
  );
});
